"use client";

import LogoNight from "@/assets/images/logoDark.png";
import LogoDay from "@/assets/images/logoLight.png";
import { DropdownProfile, Icon, Image } from "@/components";
import { UseAuth } from "@/hooks";
import { DictionaryProps } from "@/types";
import { clearFormStorage } from "@/utils/clearFormStorage";
import { Copy, LogOut } from "lucide-react";
import { useSession } from "next-auth/react";
import { useTheme } from "next-themes";
import { StaticImageData } from "next/image";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import * as S from "./styles";

interface MobileDrawerProps extends DictionaryProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  onOpenEditProfileModal: () => void;
}

const MobileDrawer: React.FC<MobileDrawerProps> = ({
  isOpen,
  onClose,
  children,
  onOpenEditProfileModal,
  dict,
}) => {
  const theme = useTheme();
  const { handleLogout } = UseAuth();
  const { data: session } = useSession();
  const [copied, setCopied] = useState(false);
  const [copiedPlan, setCopiedPlan] = useState(false);
  const Logo = React.useMemo(
    () => (theme.theme === "dark" ? LogoNight : (LogoDay as StaticImageData)),
    [theme.theme],
  );

  const url = useMemo(
    () =>
      `${process.env.NEXT_PUBLIC_AGENDA_URL}/${session?.user?.user?.establishment?.slug}`,
    [session?.user?.user?.establishment?.slug],
  );
  const urlPlanos = useMemo(
    () =>
      `${process.env.NEXT_PUBLIC_AGENDA_URL}/${session?.user?.user?.establishment?.slug}/planos`,
    [session?.user?.user?.establishment?.slug],
  );

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copiado com sucesso!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Erro ao copiar o link");
    }
  }, [url]);

  const handleCopyPlanos = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(urlPlanos);
      setCopiedPlan(true);
      toast.success("Link dos planos copiado com sucesso!");
      setTimeout(() => setCopiedPlan(false), 2000);
    } catch (err) {
      toast.error("Erro ao copiar o link dos planos");
    }
  }, [urlPlanos]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleLogoutClick = () => {
    clearFormStorage();
    handleLogout();
  };

  return (
    <>
      <S.Overlay $isOpen={isOpen} onClick={onClose} />
      <S.Drawer $isOpen={isOpen}>
        <S.DrawerHeader>
          <S.LogoLink href="./">
            <Image src={Logo} />
          </S.LogoLink>
          <S.CloseButton onClick={onClose}>
            <Icon name="chevronRight" />
          </S.CloseButton>
        </S.DrawerHeader>
        <S.DrawerUserSection>
          <S.ControlsContainer>
            <DropdownProfile onOpenModal={onOpenEditProfileModal} />
            <S.LogoutButton onClick={handleLogoutClick}>
              <LogOut size={18} />
              <S.LogoutText>Sair</S.LogoutText>
            </S.LogoutButton>
          </S.ControlsContainer>
        </S.DrawerUserSection>
        <div className="px-4 py-3 bg-blue-50 dark:bg-blue-950/30 border-t border-blue-200 dark:border-blue-800/50"></div>
        <S.DrawerContent>{children}</S.DrawerContent>
      </S.Drawer>
    </>
  );
};

export default MobileDrawer;
